import { onUnmounted, readonly, ref, type Ref } from 'vue';
import { isInServiceArea, MONACO_CENTER, type GeoPoint } from '@taxi/shared';

export interface GeolocationOptions {
	/** Suit la position en continu (mode conduite) au lieu d'un relevé unique. */
	watch?: boolean;
	highAccuracy?: boolean;
	/** Position de repli quand le GPS est refusé ou indisponible. */
	fallback?: GeoPoint | null;
	/** Rejette les points hors zone de service (dérive GPS, VPN). */
	restrictToServiceArea?: boolean;
}

export interface GeolocationApi {
	position: Readonly<Ref<GeoPoint | null>>;
	error: Readonly<Ref<string | null>>;
	loading: Readonly<Ref<boolean>>;
	/** Vrai si la position affichée a été corrigée à la main par l'utilisateur. */
	manual: Readonly<Ref<boolean>>;
	locate: () => Promise<GeoPoint | null>;
	start: () => void;
	stop: () => void;
	/** Corrige la position — bouton « ajuster ma position » sur la carte. */
	setManual: (point: GeoPoint) => void;
	clearManual: () => void;
}

/**
 * Géolocalisation de l'appareil.
 *
 * Utilise l'API navigateur, disponible telle quelle dans la WebView Capacitor
 * une fois les permissions natives accordées — aucun code spécifique à la
 * plateforme n'est donc nécessaire ici.
 *
 * Une position corrigée manuellement gagne toujours sur le GPS : tant que
 * l'utilisateur n'a pas annulé sa correction, le suivi ne l'écrase pas. C'est ce
 * qui rend le bouton d'ajustement utile là où le GPS dérive — rues encaissées,
 * parkings souterrains, halls d'hôtels.
 */
export function useGeolocation(options: GeolocationOptions = {}): GeolocationApi {
	const position = ref<GeoPoint | null>(options.fallback ?? null);
	const error = ref<string | null>(null);
	const loading = ref(false);
	const manual = ref(false);

	let watchId: number | null = null;

	const isAvailable = (): boolean =>
		typeof navigator !== 'undefined' && 'geolocation' in navigator;

	const toPoint = (result: GeolocationPosition): GeoPoint => ({
		lat: result.coords.latitude,
		lng: result.coords.longitude,
		accuracy: result.coords.accuracy ?? null,
		heading: Number.isFinite(result.coords.heading) ? result.coords.heading : null,
		speed: Number.isFinite(result.coords.speed) ? result.coords.speed : null,
		at: new Date(result.timestamp).toISOString(),
	});

	const accept = (point: GeoPoint): boolean => {
		if (options.restrictToServiceArea && !isInServiceArea(point)) {
			error.value = 'Position hors de la zone desservie.';
			return false;
		}
		return true;
	};

	const describeError = (positionError: GeolocationPositionError): string => {
		switch (positionError.code) {
			case positionError.PERMISSION_DENIED:
				return 'Localisation refusée. Indiquez votre position manuellement.';
			case positionError.POSITION_UNAVAILABLE:
				return 'Position indisponible. Vérifiez votre GPS.';
			case positionError.TIMEOUT:
				return 'La localisation prend trop de temps.';
			default:
				return 'Localisation impossible.';
		}
	};

	const locate = (): Promise<GeoPoint | null> =>
		new Promise((resolve) => {
			if (!isAvailable()) {
				error.value = 'Localisation non disponible sur cet appareil.';
				resolve(position.value);
				return;
			}
			loading.value = true;
			navigator.geolocation.getCurrentPosition(
				(result) => {
					loading.value = false;
					const point = toPoint(result);
					if (accept(point) && !manual.value) {
						error.value = null;
						position.value = point;
					}
					resolve(position.value);
				},
				(positionError) => {
					loading.value = false;
					error.value = describeError(positionError);
					resolve(position.value);
				},
				{
					enableHighAccuracy: options.highAccuracy ?? true,
					timeout: 10_000,
					maximumAge: 5_000,
				},
			);
		});

	const start = (): void => {
		if (!isAvailable() || watchId !== null) {
			return;
		}
		watchId = navigator.geolocation.watchPosition(
			(result) => {
				const point = toPoint(result);
				// La correction manuelle prime : on ne l'écrase jamais avec le GPS.
				if (!manual.value && accept(point)) {
					error.value = null;
					position.value = point;
				}
			},
			(positionError) => {
				error.value = describeError(positionError);
			},
			{ enableHighAccuracy: options.highAccuracy ?? true, timeout: 20_000, maximumAge: 2_000 },
		);
	};

	const stop = (): void => {
		if (watchId !== null) {
			navigator.geolocation.clearWatch(watchId);
			watchId = null;
		}
	};

	const setManual = (point: GeoPoint): void => {
		manual.value = true;
		error.value = null;
		position.value = { ...point, accuracy: null, at: new Date().toISOString() };
	};

	const clearManual = (): void => {
		manual.value = false;
		void locate();
	};

	if (options.watch) {
		start();
	}
	onUnmounted(stop);

	return {
		position: readonly(position) as Readonly<Ref<GeoPoint | null>>,
		error: readonly(error),
		loading: readonly(loading),
		manual: readonly(manual),
		locate,
		start,
		stop,
		setManual,
		clearManual,
	};
}

/** Position de repli : le centre de la Principauté. */
export const DEFAULT_POSITION: GeoPoint = { ...MONACO_CENTER, accuracy: null, at: null };
